import qs from 'qs';
import { api } from '../configs/axiosConfigs';
import {
  BetType,
  HotEventDTO,
  LeagueDTO,
  MarketType,
  OddDTO,
  OddFilterDTO,
  OddsResponseDTO,
} from '../../interface/odds';
import { AxiosRequestConfig } from 'axios';
import { Utils } from '@supersports/styleguide';

function removeRedundantInFilter(filter: OddFilterDTO) {
  if (filter?.MarketType === MarketType.All) delete filter.MarketType;
  if (filter?.BetTypes?.includes(BetType.OUT)) {
    delete filter.MarketType;
    delete filter.MarketFilter;
  }

  if (!filter.SearchKey) {
    delete filter.SearchKey;
  }

  if (!filter.LeagueID) {
    delete filter.LeagueID;
  }
}

export const OddsAPI = {
  getOdds: async ({
    filter: _filter,
    version,
  }: {
    filter: OddFilterDTO;
    version?: number;
  }): Promise<OddDTO[] | OddsResponseDTO> => {
    let endpoint = '/api/v1/odd-management';

    const filter = { ..._filter };

    if (filter?.MarketType === MarketType.Early && filter?.BetTypes && filter?.BetTypes[0] == BetType.PAR) {
      filter.TimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      endpoint = '/api/v1/odd-management/parlay';
    }

    removeRedundantInFilter(filter);
    const response = await api.request({
      url: endpoint,
      method: 'GET',
      params: {
        ...filter,
        Lang: Utils.convertToLocale(localStorage.getItem('lang') ?? 'en-US'),
        Version: version,
      },
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    });

    return response.data;
  },
  getOddInfoByMatchIds: async (MatchIDs: number[], Lang: string = 'en-US'): Promise<OddDTO[]> => {
    const response = await api.request({
      url: '/api/v1/odd-management/get-odd-info-by-match-ids',
      method: 'GET',
      params: { MatchIDs, Lang },
      paramsSerializer: (params) => {
        if (!params.MatchIDs.length) {
          params.MatchIDs = [''];
        }
        return qs.stringify(params, { arrayFormat: 'repeat', allowDots: true });
      },
    });

    return response.data;
  },

  getOddInfoByMatchId: async (MatchID: number, Lang: string = 'en-US'): Promise<OddDTO> => {
    const response = await api.request({
      url: '/api/v1/odd-management/get-odd-info-by-match-id',
      method: 'GET',
      params: { MatchID, Lang },
    });

    return response.data.Data;
  },

  getOddInfoByMatchGroupId: async (MatchGroupID: string, Lang: string = 'en-US'): Promise<OddDTO> => {
    const response = await api.request({
      url: '/api/v1/odd-management/get-odd-info-by-match-group-id',
      method: 'GET',
      params: { MatchGroupID, SpecialTypes: 'CORNERS', Lang },
      paramsSerializer: (params) => {
        if (!params.MatchGroupID.length) {
          params.MatchGroupID = [''];
        }
        return qs.stringify(params, { arrayFormat: 'repeat', allowDots: true });
      },
    });

    if (!response.data.Data) {
      throw new Error('No data returned from the API');
    }

    return response.data.Data;
  },
  getAvailableMatches: async (
    {
      filter: _filter,
    }: {
      filter: Omit<OddFilterDTO, 'SportType'>;
    },
    Lang: string = Utils.convertToLocale(localStorage.getItem('lang') ?? 'en-US'),
    searchType: string = 'All'
  ): Promise<Record<string, number>> => {
    const filter = { ..._filter } as OddFilterDTO;
    removeRedundantInFilter(filter);

    const response = await api.request({
      url: '/api/v1/odd-management/count-available-match',
      method: 'GET',
      params: { ...filter, Lang, searchType },
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    });
    return response.data;
  },
  getAvailableLeagues: async (
    { filter: _filter }: { filter: Partial<OddFilterDTO> },
    options?: AxiosRequestConfig
  ): Promise<LeagueDTO[]> => {
    const filter = { ..._filter } as OddFilterDTO;
    let TimeZone = undefined;
    if (filter?.MarketType === MarketType.Early && filter?.BetTypes && filter?.BetTypes[0] == BetType.PAR) {
      TimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    removeRedundantInFilter(filter);

    const response = await api.request({
      url: '/api/v1/odd-management/available-league',
      method: 'GET',
      params: { ...filter, TimeZone },
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
      ...options,
    });
    return response.data;
  },
  getHotEvents: async (params?: { limit?: number }): Promise<HotEventDTO[]> => {
    const limit = params?.limit ?? 10;
    const response = await api.request({
      url: '/api/v1/odd-management/get-hot-event',
      params: { Limit: limit },
      method: 'GET',
    });
    return response.data;
  },
  /**
   * Retrieves the available data based on the given filter.
   *
   * @param {Object} filter - The filter object containing the parameters used to filter the data.
   * @param {Partial<OddFilterDTO>} filter.filter - The filter criteria to be applied.
   * @return {Promise<LeagueDTO[]>} A promise that resolves to an array of LeagueDTO objects representing the available data.
   */
  getAvailableDates: async ({ filter }: { filter: Partial<OddFilterDTO> }): Promise<[number, number, number][]> => {
    const response = await api.request({
      url: '/api/v1/odd-management/available-date',
      method: 'GET',
      params: { ...filter },
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    });
    return response.data;
  },
  getLiveStreamURL: async ({
    MatchGroupID,
    LiveID,
    token,
  }: {
    MatchGroupID: string;
    LiveID: string;
    token: string;
  }): Promise<string> => {
    const response = await api.request({
      url: '/api/v1/odd-management/live-stream-url',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: { MatchGroupID, LiveID },
      paramsSerializer: (params) => {
        return qs.stringify(params);
      },
    });
    return response?.data?.Data?.Url;
  },
  getLiveChat: async ({
    type,
    key,
    token,
    merchant,
  }: {
    type: string;
    key: string;
    token: string;
    merchant: string;
  }): Promise<string> => {
    const response = await api.request({
      url: '/api/v1/merchant-setting/value',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        MerchantCode: merchant,
      },
      params: { type, key },
      paramsSerializer: (params) => {
        return qs.stringify(params);
      },
    });
    return response?.data?.Data;
  },
};
